import { Clock, Hash, Calendar, Key, CreditCard, Car, Phone } from 'lucide-react';
import { InfoSection, InfoField } from '@/core/components/ui/detail-blade';
import { Badge } from '@/core/components/ui/badge';
import { KioskSessionGridRow, KioskSessionDetail } from '../types/kiosk-session-types';

interface SessionInfoSectionProps {
  session: KioskSessionGridRow | null;
  detail: KioskSessionDetail | undefined;
}

export function SessionInfoSection({ session, detail }: SessionInfoSectionProps) {
  if (!detail) {
    return null;
  }

  const formatAuthMethod = (method: string | undefined) => {
    if (!method || method === 'UNKNOWN') return 'N/A';
    const methodMap: Record<string, string> = {
      'AUTH_REQUEST': 'App / QR Code',
      'COMMAND': 'Remote Command',
      'WHITELIST': 'RFID Card'
    };
    return methodMap[method] || method;
  };

  const formatTokenType = (tokenType: string | undefined) => {
    if (!tokenType) return 'N/A';
    return tokenType
      .replace('APP_USER', 'App User')
      .replace('RFID', 'RFID Card')
      .replace(/_/g, ' ');
  };

  const getStatusVariant = (status: string): 'default' | 'success' | 'destructive' | 'secondary' => {
    switch (status?.toUpperCase()) {
      case 'COMPLETED': return 'success';
      case 'ACTIVE': return 'default';
      case 'FAILED':
      case 'INVALID': return 'destructive';
      default: return 'secondary';
    }
  };

  return (
    <InfoSection
      title="Session Information"
      icon={Hash}
      collapsible={true}
      defaultExpanded={true}
      storageKey="kioskSessionDetail.sessionInfo.expanded"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <InfoField
            label="Session ID"
            value={session?.session_id || detail.session_id}
            type="id"
            copyable
          />

          <div className="flex items-center justify-between py-1">
            <span className="text-sm text-muted-foreground">Status</span>
            <Badge variant={getStatusVariant(session?.status || detail.status)}>
              {session?.status || detail.status}
            </Badge>
          </div>

          <InfoField
            label="EVSE ID"
            value={session?.evse_id || detail.evse_id}
            copyable
          />

          <InfoField
            label="Connector ID"
            value={session?.connector_id || detail.connector_id}
            copyable
          />

          {detail.transaction_id && (
            <InfoField
              label="Transaction ID"
              value={detail.transaction_id.toString()}
              type="id"
              copyable
            />
          )}
        </div>

        <div className="space-y-3">
          <InfoField
            label="Start Time"
            value={detail.start_time}
            type="date"
            icon={Calendar}
          />

          <InfoField
            label="Stop Time"
            value={detail.stop_time}
            type="date"
            icon={Calendar}
          />

          <InfoField
            label="Duration"
            value={detail.duration}
            icon={Clock}
          />

          {detail.auth_method && detail.auth_method !== 'UNKNOWN' && (
            <InfoField
              label="Auth Method"
              value={formatAuthMethod(detail.auth_method)}
              icon={Key}
            />
          )}

          {detail.token_type && (
            <InfoField
              label="Token Type"
              value={formatTokenType(detail.token_type)}
              icon={CreditCard}
            />
          )}
        </div>
      </div>

      {/* Additional session details row */}
      {(detail.plate || detail.phone_number || detail.remark) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t">
          {detail.plate && (
            <InfoField
              label="License Plate"
              value={detail.plate}
              icon={Car}
            />
          )}

          {detail.phone_number && (
            <InfoField
              label="Phone Number"
              value={detail.phone_number}
              icon={Phone}
              copyable
            />
          )}

          {detail.remark && (
            <div className="md:col-span-2">
              <InfoField
                label="Remark"
                value={detail.remark}
              />
            </div>
          )}
        </div>
      )}
    </InfoSection>
  );
}